import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Icon from '../common/Icon';
import { NotificationBell as SharedNotificationBell } from '@copilot/ui';
import * as notificationApi from '../../services/notificationApi';
import { reportError } from '../../services/errorTracking';
import { NOTIFICATIONS_REFRESH_EVENT } from '../../utils/notificationEvents';

const PAGE_SIZE = 10;
// No dedicated unread-count endpoint - GET /api/notifications already
// returns unread_count on every page, so polling reuses it with
// page_size=1 to keep the background request cheap while the dropdown
// is closed. Same interval-polling shape as AuthContext's session
// heartbeat.
const POLL_MS = 30 * 1000;

// One helper, one lookup table, rather than a per-notification-type
// switch scattered through handleItemClick - every notification's
// destination is decided by its reference_type alone (brief: "Prefer a
// helper... for Web and an equivalent helper for Desktop" - see
// desktop/src/components/NotificationBell.tsx's REFERENCE_ROUTES for
// that counterpart, kept in sync by hand since the two apps have
// different router shapes).
//
// "meeting_still_active" is a meeting notification like any other and
// lands on the read-only details page (/app/meeting/:id) - deliberately
// NOT on /live: opening the live page would reconnect audio, and this
// flow never resumes a meeting. The details page is where End Meeting is.
// subscription_change_request is admin-only (see services/
// subscription_service.py's request_plan_change()) - a normal user
// never receives one, so it's safe to map unconditionally here even
// though this component is shared between AppShell and AdminShell.
// security_alert notifications about a device/user event now carry
// reference_type "device"/"user" (services/device_service.py, services/
// admin_service.py) - a bare security_alert with no reference_type
// still just gets read, since there is nowhere more specific to send it.
const REFERENCE_ROUTES = {
  meeting: (id) => `/app/meeting/${id}`,
  // Billing was deliberately moved out from under Settings (see App.jsx)
  // - subscription/payment/refund notifications belong on the page that
  // actually shows plan/payment state, not the general settings page.
  subscription: () => '/app/billing',
  payment: () => '/app/billing',
  payment_subscription: () => '/app/billing',
  subscription_change_request: () => '/admin/pricing',
  document: () => '/app/documents',
  device: () => '/app/settings',
  user: () => '/app/settings',
};

/** Shared by Web's handleItemClick and, structurally, by Desktop's
 * equivalent - resolves a notification to a destination, or null when
 * there is nowhere more specific than "just mark it read" (an
 * unreferenced security_alert, or a reference_type this app does not
 * (yet) map). A missing/deleted entity is not this function's problem:
 * every mapped destination is a LIST/overview page (Documents, Meeting
 * Details still resolves a meeting id itself and 404s gracefully like
 * any other direct link), never a page that assumes the entity still
 * exists - see brief Task 31. */
function getNotificationDestination(notification) {
  const buildRoute = REFERENCE_ROUTES[notification.reference_type];
  if (!buildRoute || !notification.reference_id) return null;
  return buildRoute(notification.reference_id);
}

// SharedNotificationBell renders isRead/createdAt, but callbacks
// (onItemClick/onDelete) receive this same object back - spreading the
// original notification (rather than picking just the display fields)
// keeps is_read/reference_type/reference_id available to handleItemClick/
// handleDelete without a second lookup.
function toSharedItem(item) {
  return { ...item, isRead: item.is_read, createdAt: item.created_at };
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  // Responses can arrive out of order (the mount poll, the poll the
  // active-meeting check triggers, the list load, the 30s interval). A
  // slow OLDER response - e.g. "0 unread" computed before the reminder
  // existed - must never overwrite a newer one, so each kind of request
  // only applies its result if no newer request of that kind started.
  const unreadSeqRef = useRef(0);
  const listSeqRef = useRef(0);

  // Cheap background refresh of just the unread badge - never touches
  // the loaded item list, so it can't clobber state while the dropdown
  // is open and the user is mid-interaction with it.
  const pollUnreadCount = useCallback(async () => {
    unreadSeqRef.current += 1;
    const seq = unreadSeqRef.current;
    try {
      const data = await notificationApi.listNotifications({
        page: 1,
        pageSize: 1,
      });
      if (seq === unreadSeqRef.current) {
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      reportError('notifications_poll', error);
    }
  }, []);

  useEffect(() => {
    pollUnreadCount();
    const interval = setInterval(pollUnreadCount, POLL_MS);
    return () => clearInterval(interval);
  }, [pollUnreadCount]);

  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    unreadSeqRef.current += 1;
    listSeqRef.current += 1;
    const unreadSeq = unreadSeqRef.current;
    const listSeq = listSeqRef.current;
    try {
      const data = await notificationApi.listNotifications({
        page: 1,
        pageSize: PAGE_SIZE,
      });
      if (listSeq === listSeqRef.current) {
        setItems(data.items);
        setPage(data.page);
        setTotalPages(data.total_pages);
        setLoaded(true);
      }
      if (unreadSeq === unreadSeqRef.current) {
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      reportError('notifications_load', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Something server-side just changed the notification list (the
  // active-meeting check on app open, a meeting ended on its details
  // page) - see utils/notificationEvents.js. Refreshes the badge, and the
  // list too if the dropdown has been opened before, so a stale row is
  // never left showing.
  useEffect(() => {
    const handleRefresh = () => {
      if (loaded) {
        loadFirstPage();
      } else {
        pollUnreadCount();
      }
    };

    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, handleRefresh);
    return () =>
      window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, handleRefresh);
  }, [loaded, loadFirstPage, pollUnreadCount]);

  const refreshUnreadCount = useCallback(async () => {
    const data = await notificationApi.listNotifications({
      page: 1,
      pageSize: 1,
    });
    setUnreadCount(data.unread_count);
  }, []);

  const handleOpenChange = (willOpen) => {
    if (willOpen) {
      loadFirstPage();
    }
    setOpen(willOpen);
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await notificationApi.listNotifications({
        page: page + 1,
        pageSize: PAGE_SIZE,
      });
      setItems((prev) => [...prev, ...data.items]);
      setPage(data.page);
      setTotalPages(data.total_pages);
    } catch (error) {
      reportError('notifications_load_more', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleMarkAllRead = async () => {
    const previousItems = items;
    const previousUnread = unreadCount;
    setItems((prev) => prev.map((item) => ({ ...item, is_read: true })));
    setUnreadCount(0);
    try {
      await notificationApi.markAllNotificationsRead();
      await refreshUnreadCount();
    } catch (error) {
      reportError('notifications_mark_all_read', error);
      setItems(previousItems);
      setUnreadCount(previousUnread);
    }
  };

  const handleItemClick = async (item) => {
    if (!item.is_read) {
      setItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, is_read: true } : i))
      );
      setUnreadCount((count) => Math.max(0, count - 1));
      notificationApi
        .markNotificationRead(item.id)
        .then(refreshUnreadCount)
        .catch((error) => {
          reportError('notifications_mark_read', error);
          setItems((prev) =>
            prev.map((i) => (i.id === item.id ? { ...i, is_read: false } : i))
          );
          setUnreadCount((count) => count + 1);
        });
    }

    const destination = getNotificationDestination(item);
    if (destination) {
      setOpen(false);
      navigate(destination);
    }
  };

  const handleDelete = async (item) => {
    const previousItems = items;
    const previousUnread = unreadCount;
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    if (!item.is_read) setUnreadCount((count) => Math.max(0, count - 1));
    try {
      await notificationApi.deleteNotification(item.id);
      await refreshUnreadCount();
    } catch (error) {
      reportError('notifications_delete', error);
      setItems(previousItems);
      setUnreadCount(previousUnread);
    }
  };

  return (
    <SharedNotificationBell
      items={items.map(toSharedItem)}
      unreadCount={unreadCount}
      open={open}
      onOpenChange={handleOpenChange}
      loading={loading}
      loadingMore={loadingMore}
      hasMore={page < totalPages}
      onLoadMore={handleLoadMore}
      onMarkAllRead={handleMarkAllRead}
      onItemClick={handleItemClick}
      onDelete={handleDelete}
      bellIcon={<Icon name="bell" />}
      deleteIcon={<Icon name="x" size={14} />}
    />
  );
}

export default NotificationBell;
